import { debounce } from "../../utils/helpers";

const preventEvent = event => {
  event.stopPropagation();
  event.preventDefault();
};

const preventMouseEvents = () => {
  document.addEventListener("click", preventEvent, true);
  document.addEventListener("dblclick", preventEvent, true);
  document.addEventListener("contextmenu", preventEvent, true);
  document.addEventListener("auxclick", preventEvent, true);
};

const allowMouseEvents = () => {
  document.removeEventListener("click", preventEvent, true);
  document.removeEventListener("dblclick", preventEvent, true);
  document.removeEventListener("contextmenu", preventEvent, true);
  document.removeEventListener("auxclick", preventEvent, true);
};

/*
  Hide step tooltip primary button pulsing border.
 */
const hidePrimaryButtonPulsingBorder = () => {
  const primaryButtonWithPulsingBorder = document.querySelector(
    ".tour-button-primary.tour-show-pulsing-border"
  );
  if (primaryButtonWithPulsingBorder) {
    primaryButtonWithPulsingBorder.classList.remove("tour-show-pulsing-border");
  }
};

/*
  Get buttons element for the step.
 */
const getStepButtons = (
  primaryText,
  primaryAction,
  secondaryText,
  secondaryAction
) => {
  const buttons = [];
  if (secondaryText) {
    buttons.push({
      classes: "tour-button-secondary",
      text: secondaryText,
      action() {
        hidePrimaryButtonPulsingBorder();
        if (secondaryAction) {
          secondaryAction(this);
        } else {
          return this.cancel();
        }
      }
    });
  }
  buttons.push({
    classes: "tour-button-primary",
    text: primaryText,
    action() {
      hidePrimaryButtonPulsingBorder();
      if (primaryAction) {
        primaryAction(this);
      } else {
        return this.next();
      }
    }
  });
  return { buttons };
};

/*
  Get a promise that will resolve when check function returns true
 */
const getStepBeforeShowPromiseWithCheck = function (
  checkFunction,
  timeoutMs = 100
) {
  return {
    beforeShowPromise: function () {
      const step = this;
      return new Promise(resolve => {
        const intervalId = setInterval(() => {
          if (checkFunction(step)) {
            clearInterval(intervalId);
            setTimeout(() => {
              resolve();
            }, timeoutMs);
          }
        }, 100);
      });
    }
  };
};

/*
  Set the pulsing border element styles to position it correctly.
 */
const setPulsingBorderStyle = (
  pulsingBorderElement,
  targetElement,
  overlayPadding,
  noOverlayRadius = false,
  borderRadius
) => {
  const targetElementOffset = targetElement.getBoundingClientRect();
  pulsingBorderElement.setAttribute(
    "style",
    `
          top: ${
            targetElementOffset.top -
            (overlayPadding === 0 ? 0 : overlayPadding + 1)
          }px;
          left: ${
            targetElementOffset.left -
            (overlayPadding === 0 ? 0 : overlayPadding + 1)
          }px;
          width: ${
            targetElement.clientWidth +
            (overlayPadding === 0 ? 0 : (overlayPadding + 0.5) * 2)
          }px;
          height: ${
            targetElement.clientHeight +
            (overlayPadding === 0 ? 0 : (overlayPadding + 0.5) * 2)
          }px;
          ${
            noOverlayRadius
              ? "border-radius: 0"
              : borderRadius
              ? `border-radius: ${borderRadius}`
              : ""
          }
        `
  );
};

/*

 */
const createPulsingBorderShowAndHide = (
  targetElementOrSelector,
  show,
  onScrollRepositionElementSelector = undefined,
  overlayPadding = false,
  noOverlayRadius = false,
  borderRadius
) => {
  /*
    Reference to a debounce function that registered on window resize,
    to clear it correctly when hiding.
   */
  let setPulsingBorderStyleDebounce = null;

  return {
    show: () => {
      allowMouseEvents();
      const pulsingBorderElement = document.querySelector(
        "#tour_pulsing_border"
      );

      const targetElement =
        typeof targetElementOrSelector === "string"
          ? document.querySelector(targetElementOrSelector)
          : targetElementOrSelector;

      if (pulsingBorderElement && targetElement) {
        setPulsingBorderStyle(
          pulsingBorderElement,
          targetElement,
          overlayPadding,
          noOverlayRadius,
          borderRadius
        );
        // Register a function to change the position on window resize
        setPulsingBorderStyleDebounce = debounce(
          () =>
            setPulsingBorderStyle(
              pulsingBorderElement,
              targetElement,
              overlayPadding,
              noOverlayRadius,
              borderRadius
            ),
          16
        );
        window.addEventListener("resize", setPulsingBorderStyleDebounce);
        if (onScrollRepositionElementSelector) {
          const element = document.querySelector(
            onScrollRepositionElementSelector
          );
          if (element) {
            element.addEventListener("scroll", setPulsingBorderStyleDebounce);
          }
        }
        if (show) {
          pulsingBorderElement.classList.add("tour-show-pulsing-border");
        }
      }
    },
    hide: () => {
      preventMouseEvents();
      const pulsingBorderElement = document.querySelector(
        "#tour_pulsing_border"
      );
      if (pulsingBorderElement) {
        if (setPulsingBorderStyleDebounce !== null) {
          window.removeEventListener("resize", setPulsingBorderStyleDebounce);
        }
        pulsingBorderElement.classList.remove("tour-show-pulsing-border");
        pulsingBorderElement.removeAttribute("style");
        if (onScrollRepositionElementSelector) {
          const element = document.querySelector(
            onScrollRepositionElementSelector
          );
          if (element) {
            element.removeEventListener(
              "scroll",
              setPulsingBorderStyleDebounce
            );
          }
        }
      }
    }
  };
};

/*
  Get step when and add logic for positioning and showing the pulsing border
 */
const getStepWhenWithPulsingBorder = ({
  targetSelector,
  show = true,
  overlayPadding = 10,
  noOverlayRadius = false,
  onShow,
  onHide,
  onScrollRepositionElementSelector,
  borderRadius = undefined
}) => {
  let pulsingBorderShowAndHide = null;
  return {
    when: {
      show: function () {
        pulsingBorderShowAndHide = createPulsingBorderShowAndHide(
          targetSelector ? document.querySelector(targetSelector) : this.target,
          show,
          onScrollRepositionElementSelector,
          overlayPadding,
          noOverlayRadius,
          borderRadius
        );
        pulsingBorderShowAndHide.show();
        if (onShow) {
          onShow();
        }
      },
      hide: function () {
        pulsingBorderShowAndHide.hide();
        if (onHide) {
          onHide();
        }
      }
    }
  };
};

export {
  getStepWhenWithPulsingBorder,
  getStepBeforeShowPromiseWithCheck,
  getStepButtons,
  createPulsingBorderShowAndHide,
  preventEvent,
  preventMouseEvents,
  allowMouseEvents
};
